import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useTargetPublishedChecklists } from "../../../lib/api";
import { useToast } from "../../../lib/use-toast";

/**
 * ImportTypeSelector Component
 *
 * Allows user to choose between importing as a new checklist or as a revision
 * If revision is selected, shows a list of published checklists to select from
 */
interface ImportTypeSelectorProps {
  className?: string;
}

export function ImportTypeSelector({ className }: ImportTypeSelectorProps) {
  const {
    targetFacilityId,
    targetUseCaseId,
    targetServiceId,
    targetImportType,
    setTargetImportType,
    targetChecklistId,
    setTargetChecklistId,
    setTargetChecklistName,
    setTargetAuthorId,
    selectedChecklists,
    setSelectedChecklists
  } = useWorkflowStore();

  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");

  // Fetch published checklists only when revision is selected. Central-MU: pass
  // targetServiceId to route through /api/instances/{id}/published-checklists.
  const {
    data: publishedChecklists = [],
    isLoading,
    error
  } = useTargetPublishedChecklists(
    targetImportType === 'revision' ? targetFacilityId : null,
    targetImportType === 'revision' ? targetUseCaseId : null,
    targetServiceId,
  );

  // Filter checklists based on search term
  const filteredChecklists = publishedChecklists.filter((checklist: any) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      checklist.code?.toLowerCase().includes(searchLower) ||
      checklist.name?.toLowerCase().includes(searchLower)
    );
  });

  // Reset checklist selection when import type changes
  useEffect(() => {
    setTargetChecklistId(null);
    setTargetChecklistName(null);
    setTargetAuthorId(null);
    setSearchTerm("");
  }, [targetImportType, setTargetChecklistId, setTargetChecklistName, setTargetAuthorId]);

  // Reset when facility or use case changes
  useEffect(() => {
    setTargetImportType(null);
    setTargetChecklistId(null);
    setTargetChecklistName(null);
    setSearchTerm("");
  }, [targetFacilityId, targetUseCaseId, setTargetImportType, setTargetChecklistId, setTargetChecklistName]);

  const isDisabled = !targetFacilityId || !targetUseCaseId;

  const handleImportTypeChange = (value: string) => {
    const importType = value as 'new' | 'revision';
    setTargetImportType(importType);

    // A revision maps a single source checklist onto one existing target checklist, so
    // it can only carry one source checklist. If the user picked several in the source
    // step, keep the first and let them know.
    if (importType === 'revision' && selectedChecklists.length > 1) {
      const kept = selectedChecklists[0];
      setSelectedChecklists([kept]);
      toast({
        title: "Revision supports a single checklist",
        description: `Kept only "${kept.name}". Revision can migrate one checklist at a time.`,
      });
    }
  };

  const getPlaceholderText = () => {
    if (!targetFacilityId || !targetUseCaseId) return "Select facility and use case first";
    return "Select import type";
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-4">
        <div className="space-y-2">
          <Label htmlFor="import-type">Import Type</Label>
          <Select
            value={targetImportType || ""}
            onValueChange={handleImportTypeChange}
            disabled={isDisabled}
          >
            <SelectTrigger id="import-type">
              <SelectValue placeholder={getPlaceholderText()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New Checklist</SelectItem>
              <SelectItem value="revision">Revision of existing checklist</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {targetImportType === 'revision' && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Importing as a revision removes the trained users of the selected checklist. They will need to be trained again on the new revision.
            </span>
          </div>
        )}

        {/* Show checklist list only when revision is selected */}
        {targetImportType === 'revision' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Published Checklists</h3>
              <span className="text-sm text-muted-foreground">
                {targetChecklistId ? '1 selected' : '0 selected'}
              </span>
            </div>

            {/* Search bar */}
            <Input
              type="text"
              placeholder="Search by checklist code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />

            {/* Checklist list */}
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Loading published checklists...
                </div>
              ) : error ? (
                <div className="p-4 text-center text-destructive">
                  Error: {error instanceof Error ? error.message : "Failed to load checklists"}
                </div>
              ) : filteredChecklists.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {searchTerm ? `No checklists found matching "${searchTerm}"` : "No published checklists available"}
                </div>
              ) : (
                // Radio group — revision targets exactly one existing checklist.
                <RadioGroup
                  value={targetChecklistId ?? ""}
                  onValueChange={(id) => {
                    setTargetChecklistId(id);
                    const picked = publishedChecklists.find((c: any) => c.id === id);
                    setTargetChecklistName(picked?.name ?? null);
                  }}
                  className="gap-0"
                >
                  {filteredChecklists.map((checklist: any) => (
                    <div
                      key={checklist.id}
                      className="flex items-start space-x-3 p-4 border-b last:border-b-0 hover:bg-accent"
                    >
                      <RadioGroupItem
                        id={`revise-${checklist.id}`}
                        value={checklist.id}
                        className="mt-1"
                      />
                      <Label
                        htmlFor={`revise-${checklist.id}`}
                        className="flex-1 space-y-1 cursor-pointer font-normal"
                      >
                        <div className="font-medium">{checklist.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {checklist.code} • v{checklist.version || '1'}
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>

            {targetImportType === 'revision' && !targetChecklistId && filteredChecklists.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Select which published checklist you want to create a revision of
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}